import { useState } from "react";
import { Box, Button, Collapse, Typography } from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SectionCard } from "@/components/DesignSystem";
import { chartColors, colors, riskColors } from "@/theme/eventsTokens.ts";
import { formatSnapshotFreshness } from "@/utils/snapshotFreshness.ts";
import { relativeTime } from "@/utils/relativeTime.ts";
import type { CredentialHealthData, ExposureEntry } from "@/types";
import AccountDetails from "./AccountDetails.tsx";

// Failure counts below LOW read as background noise (grey); HIGH+ is a real
// campaign (red). Colour tracks absolute volume so a single stray failure never
// renders as a full red alarm.
const LOW = 5;
const HIGH = 25;
// The card sits beside the short second-factor card; showing every ranked
// account made it grow with the ranking and pushed the page off-screen.
const VISIBLE = 5;

export default function MostTargetedAccounts({ data }: { readonly data: CredentialHealthData }) {
  const entries = data.exposure_index;
  const windowDays = data.window_days;
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? entries : entries.slice(0, VISIBLE);
  const notIngesting = entries.length === 0 && data.exposure_available === false;
  const maxFailures = Math.max(1, ...entries.map((e) => e.failures));
  const lowActivity = entries.length > 0 && maxFailures < LOW;
  return (
    <SectionCard
      title="Most Targeted Accounts"
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
        Accounts under attack from hostile sources (anonymised). Expand an account for its sources and
        the full hash.
      </Typography>
      {notIngesting && <NotIngestingNote />}
      {entries.length === 0 && !notIngesting && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          No accounts targeted by hostile sources in the recent window yet.
        </Typography>
      )}
      {lowActivity && (
        <Typography variant="caption" sx={{ display: "block", mb: 1, color: riskColors.low.main, fontWeight: 600 }}>
          Low targeting activity — no account is under significant attack.
        </Typography>
      )}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {shown.map((entry, i) => (
          <AccountRow
            key={entry.username_hash}
            entry={entry}
            rank={i + 1}
            maxFailures={maxFailures}
            windowDays={windowDays}
          />
        ))}
      </Box>
      {entries.length > VISIBLE && (
        <ShowAllToggle total={entries.length} showAll={showAll} onToggle={() => setShowAll((s) => !s)} />
      )}
      {!notIngesting && <Footnote asOf={data.exposure_as_of} windowDays={windowDays} />}
    </SectionCard>
  );
}

function ShowAllToggle({
  total,
  showAll,
  onToggle,
}: {
  readonly total: number;
  readonly showAll: boolean;
  readonly onToggle: () => void;
}) {
  return (
    <Button
      variant="outlined"
      size="small"
      onClick={onToggle}
      endIcon={
        <ExpandMoreIcon sx={{ transform: showAll ? "rotate(180deg)" : "none", transition: "transform 0.2s ease" }} />
      }
      sx={{ mt: 1.5, alignSelf: "flex-start" }}
    >
      {showAll ? `Show top ${VISIBLE}` : `Show all ${total} accounts`}
    </Button>
  );
}

function NotIngestingNote() {
  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600, mb: 0.5 }}>
        No username hashes received yet
      </Typography>
      <Typography variant="body2" color="text.secondary">
        To see which accounts attackers are targeting, include a <code>usernameHash</code> with your
        authentication requests. Once received, the most-targeted accounts will appear here.
      </Typography>
    </Box>
  );
}

// A stable name the customer can use across sessions ("the …72e494 account"),
// with a colour picked from the hash so the same account always looks the same.
function accountLabel(hash: string): string {
  return `Account …${hash.slice(-6)}`;
}

const ACCOUNT_PALETTE = [
  chartColors.primary,
  chartColors.secondary,
  chartColors.tertiary,
  chartColors.quaternary,
  chartColors.quinary,
];

function accountTint(hash: string): string {
  let h = 0;
  for (const ch of hash) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return ACCOUNT_PALETTE[h % ACCOUNT_PALETTE.length];
}

function AccountRow({
  entry,
  rank,
  maxFailures,
  windowDays,
}: {
  readonly entry: ExposureEntry;
  readonly rank: number;
  readonly maxFailures: number;
  readonly windowDays: number;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ "&:not(:last-child)": { borderBottom: `1px solid ${colors.borderLight}` } }}>
      <AccountSummary
        entry={entry}
        rank={rank}
        maxFailures={maxFailures}
        windowDays={windowDays}
        open={open}
        onToggle={() => setOpen((o) => !o)}
      />
      <Collapse in={open} unmountOnExit>
        <AccountDetails entry={entry} windowDays={windowDays} />
      </Collapse>
    </Box>
  );
}

function AccountSummary({
  entry,
  rank,
  maxFailures,
  windowDays,
  open,
  onToggle,
}: {
  readonly entry: ExposureEntry;
  readonly rank: number;
  readonly maxFailures: number;
  readonly windowDays: number;
  readonly open: boolean;
  readonly onToggle: () => void;
}) {
  const color = accountColor(entry.failures);
  const width = Math.max((entry.failures / maxFailures) * 100, 2);
  return (
    <Box
      component="button"
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      sx={{
        all: "unset",
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        width: "100%",
        py: 1,
        cursor: "pointer",
        "&:focus-visible": { outline: `2px solid ${colors.primary}`, outlineOffset: 2, borderRadius: 1 },
      }}
    >
      <Typography
        variant="caption"
        sx={{ width: 22, flexShrink: 0, fontWeight: 700, color: "text.disabled", textAlign: "right" }}
      >
        {rank}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <AccountHeader entry={entry} color={color} />
        <FailureBar width={width} color={color} />
        <AccountMeta entry={entry} windowDays={windowDays} />
      </Box>
      <ExpandMoreIcon
        sx={{
          fontSize: 20,
          color: colors.textMuted,
          flexShrink: 0,
          transform: open ? "rotate(180deg)" : "none",
          transition: "transform 0.2s ease",
        }}
      />
    </Box>
  );
}

function AccountHeader({ entry, color }: { readonly entry: ExposureEntry; readonly color: string }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5, gap: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <PersonOutlineIcon sx={{ fontSize: 16, color: accountTint(entry.username_hash), flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
        >
          {accountLabel(entry.username_hash)}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 700, flexShrink: 0, color }}>
        {entry.failures.toLocaleString()}
      </Typography>
    </Box>
  );
}

function FailureBar({ width, color }: { readonly width: number; readonly color: string }) {
  return (
    <Box sx={{ height: 4, borderRadius: 2, bgcolor: colors.backgroundMuted, overflow: "hidden", mb: 0.5 }}>
      <Box sx={{ width: `${width}%`, height: "100%", borderRadius: 2, bgcolor: color, transition: "width 0.3s ease" }} />
    </Box>
  );
}

function AccountMeta({ entry, windowDays }: { readonly entry: ExposureEntry; readonly windowDays: number }) {
  const last = relativeTime(entry.last_seen);
  const attempts = entry.failures === 1 ? "attempt" : "attempts";
  const sources = entry.sources.length === 1 ? "source" : "sources";
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "left" }}>
      {entry.failures.toLocaleString()} failed {attempts} in the last {windowDays} days · from {entry.sources.length}{" "}
      {sources}
      {last ? ` · last targeted ${last}` : ""}
    </Typography>
  );
}

function accountColor(failures: number): string {
  if (failures >= HIGH) return riskColors.high.main;
  if (failures >= LOW) return riskColors.medium.main;
  return colors.textMuted;
}

function Footnote({ asOf, windowDays }: { readonly asOf?: string | null; readonly windowDays: number }) {
  const label = formatSnapshotFreshness(asOf);
  return (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}>
      Counts invalid passwords and failed second factors, so only usernames that exist, from sources with no
      successful sign-in in the last {windowDays} days. Anonymised{label ? ` · snapshot as of ${label}` : ""}.
    </Typography>
  );
}
